import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export type ReviewTargetType = "business" | "menu_item" | "supplier";

export type PublicReview = {
  id: string;
  user_id: string;
  target_type: ReviewTargetType;
  target_id: string;
  rating: number;
  comment: string | null;
  author_name_snapshot: string | null;
  created_at: string;
  updated_at: string;
};

export type ReviewsResponse = {
  reviews: PublicReview[];
  summary: { avg: number | null; count: number };
  myReview: PublicReview | null;
};

const targetSchema = z.object({
  targetType: z.enum(["business", "menu_item", "supplier"]),
  targetId: z.string().uuid(),
});

const upsertSchema = targetSchema.extend({
  rating: z.number().int().min(1).max(10),
  comment: z.string().trim().max(2000).nullable().optional(),
});

const hideSchema = z.object({
  id: z.string().uuid(),
  hidden: z.boolean(),
  reason: z.string().trim().max(500).nullable().optional(),
});

const reportReasonEnum = z.enum([
  "spam",
  "offensive",
  "off_topic",
  "misinformation",
  "personal_data",
  "other",
]);
export type ReviewReportReason = z.infer<typeof reportReasonEnum>;
export type ReviewReportStatus = "open" | "resolved" | "dismissed";

const reportSchema = z.object({
  reviewId: z.string().uuid(),
  reason: reportReasonEnum,
  detail: z.string().trim().max(2000).nullable().optional(),
});

const resolveReportSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["resolved", "dismissed", "open"]),
  note: z.string().trim().max(500).nullable().optional(),
});

async function createPublicClient() {
  const { createClient } = await import("@supabase/supabase-js");
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

/**
 * Public list of visible reviews + summary for one entity.
 * Uses the anon-scoped publishable client — respects RLS (hidden rows excluded).
 * Signed-in callers may pass their user id via _myUserId hint; we still resolve
 * ownership via the client-side auth session for `myReview`.
 */
export const listReviews = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => targetSchema.parse(data))
  .handler(async ({ data }): Promise<ReviewsResponse> => {
    const supa = await createPublicClient();
    const { data: rows, error } = await supa
      .from("reviews")
      .select("id, user_id, target_type, target_id, rating, comment, author_name_snapshot, created_at, updated_at")
      .eq("target_type", data.targetType)
      .eq("target_id", data.targetId)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw error;

    const reviews = (rows ?? []) as PublicReview[];
    const count = reviews.length;
    const avg = count === 0 ? null : Math.round((reviews.reduce((s, r) => s + r.rating, 0) / count) * 100) / 100;
    return { reviews, summary: { avg, count }, myReview: null };
  });

export type UserReviewItem = PublicReview & {
  target_label: string | null;
  target_href: string | null;
  target_business_id: string | null;
};

export type PublicProfileInfo = {
  fullName: string | null;
  headline: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  websiteUrl: string | null;
  memberSince: string | null;
  vakantyPurchased: number;
  isVakantOwner: boolean;
};

export type PublicUserProfile = {
  userId: string;
  displayName: string | null;
  referralCode: string | null;
  profile: PublicProfileInfo | null;
  reviews: UserReviewItem[];
  summary: { avg: number | null; count: number };
};



/**
 * Public read-only user profile: reviews authored by `userId` and a display name
 * snapshot (from the most recent review). Respects RLS via anon publishable client.
 */
export const getPublicUserProfile = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ userId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }): Promise<PublicUserProfile> => {
    const supa = await createPublicClient();
    const { data: rows, error } = await supa
      .from("reviews")
      .select("id, user_id, target_type, target_id, rating, comment, author_name_snapshot, created_at, updated_at")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw error;
    const reviews = (rows ?? []) as PublicReview[];

    // Bulk resolve target labels + hrefs (best-effort; RLS may hide some).
    const bizIds = [...new Set(reviews.filter((r) => r.target_type === "business").map((r) => r.target_id))];
    const supIds = [...new Set(reviews.filter((r) => r.target_type === "supplier").map((r) => r.target_id))];
    const itemIds = [...new Set(reviews.filter((r) => r.target_type === "menu_item").map((r) => r.target_id))];

    const [bizRes, supRes, itemRes] = await Promise.all([
      bizIds.length
        ? supa.from("businesses").select("id, name").in("id", bizIds)
        : Promise.resolve({ data: [] as any[], error: null }),
      supIds.length
        ? supa.from("business_suppliers").select("id, name, business_id").in("id", supIds)
        : Promise.resolve({ data: [] as any[], error: null }),
      itemIds.length
        ? supa
            .from("menu_items")
            .select("id, menu_categories!inner(menu_id, menus!inner(id, slug, business_id))")
            .in("id", itemIds)
        : Promise.resolve({ data: [] as any[], error: null }),
    ]);

    const bizMap = new Map<string, { name: string | null }>();
    for (const b of (bizRes.data ?? []) as any[]) bizMap.set(b.id, { name: b.name });
    const supMap = new Map<string, { name: string | null; business_id: string }>();
    for (const s of (supRes.data ?? []) as any[]) supMap.set(s.id, { name: s.name, business_id: s.business_id });
    const itemMap = new Map<string, { menuSlug: string | null; business_id: string | null }>();
    for (const it of (itemRes.data ?? []) as any[]) {
      const menu = it.menu_categories?.menus;
      itemMap.set(it.id, { menuSlug: menu?.slug ?? null, business_id: menu?.business_id ?? null });
    }

    const enriched: UserReviewItem[] = reviews.map((r) => {
      if (r.target_type === "business") {
        const b = bizMap.get(r.target_id);
        return {
          ...r,
          target_label: b?.name ?? null,
          target_href: `/podnik/${r.target_id}`,
          target_business_id: r.target_id,
        };
      }
      if (r.target_type === "supplier") {
        const s = supMap.get(r.target_id);
        return {
          ...r,
          target_label: s?.name ?? null,
          target_href: s ? `/podnik/${s.business_id}/dodavatel/${r.target_id}` : null,
          target_business_id: s?.business_id ?? null,
        };
      }
      const it = itemMap.get(r.target_id);
      return {
        ...r,
        target_label: null,
        target_href: it?.business_id && it.menuSlug ? `/podnik/${it.business_id}/menu/${it.menuSlug}` : null,
        target_business_id: it?.business_id ?? null,
      };
    });

    const displayName = reviews.find((r) => r.author_name_snapshot?.trim())?.author_name_snapshot ?? null;
    const count = reviews.length;
    const avg = count === 0 ? null : Math.round((reviews.reduce((s, r) => s + r.rating, 0) / count) * 100) / 100;

    // Referral code is a public, non-sensitive attribution handle — fetch via
    // service role so profile RLS does not need to expose it broadly.
    let referralCode: string | null = null;
    let profile: PublicProfileInfo | null = null;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const [{ data: prof }, { data: roleRows }] = await Promise.all([
        supabaseAdmin
          .from("profiles")
          .select(
            "referral_code, full_name, headline, bio, city, country, website_url, created_at, vakanty_purchased, public_profile",
          )
          .eq("id", data.userId)
          .maybeSingle(),
        supabaseAdmin
          .from("user_roles")
          .select("role")
          .eq("user_id", data.userId)
          .eq("role", "vakant_owner"),
      ]);
      referralCode = ((prof as any)?.referral_code as string | null) ?? null;
      const p = prof as any;
      if (p && p.public_profile !== false) {
        profile = {
          fullName: p.full_name ?? null,
          headline: p.headline ?? null,
          bio: p.bio ?? null,
          city: p.city ?? null,
          country: p.country ?? null,
          websiteUrl: p.website_url ?? null,
          memberSince: p.created_at ?? null,
          vakantyPurchased: p.vakanty_purchased ?? 0,
          isVakantOwner: (roleRows ?? []).length > 0,
        };
      }
    } catch { /* best effort */ }

    return {
      userId: data.userId,
      displayName: profile?.fullName?.trim() || displayName,
      referralCode,
      profile,
      reviews: enriched,
      summary: { avg, count },
    };
  });



/**
 * Get the signed-in user's own review for an entity (may be hidden).
 * Kept separate so anonymous listReviews stays public + SSR-safe.
 */
export const getMyReview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => targetSchema.parse(data))
  .handler(async ({ data, context }): Promise<PublicReview | null> => {
    const { data: row, error } = await context.supabase
      .from("reviews")
      .select("id, user_id, target_type, target_id, rating, comment, author_name_snapshot, created_at, updated_at")
      .eq("user_id", context.userId)
      .eq("target_type", data.targetType)
      .eq("target_id", data.targetId)
      .maybeSingle();
    if (error) throw error;
    return (row as PublicReview | null) ?? null;
  });

/** Insert or update the signed-in user's review for an entity. */
export const upsertMyReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => upsertSchema.parse(data))
  .handler(async ({ data, context }): Promise<PublicReview> => {
    const payload = {
      user_id: context.userId,
      target_type: data.targetType,
      target_id: data.targetId,
      rating: data.rating,
      comment: data.comment?.trim() ? data.comment.trim() : null,
    };
    const { data: row, error } = await context.supabase
      .from("reviews")
      .upsert(payload, { onConflict: "user_id,target_type,target_id" })
      .select("id, user_id, target_type, target_id, rating, comment, author_name_snapshot, created_at, updated_at")
      .single();
    if (error) {
      // Defensive: race condition or a client bypassing upsert semantics.
      if ((error as { code?: string }).code === "23505") {
        throw new Error("ALREADY_RATED");
      }
      throw error;
    }
    return row as PublicReview;
  });

/** Delete the signed-in user's own review for an entity. */
export const deleteMyReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => targetSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("reviews")
      .delete()
      .eq("user_id", context.userId)
      .eq("target_type", data.targetType)
      .eq("target_id", data.targetId);
    if (error) throw error;
    return { ok: true };
  });

/** Admin / moderator: hide or unhide any review, with optional reason. */
export const adminHideReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => hideSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    const { data: isMod } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "moderator",
    });
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    const { error } = await context.supabase
      .from("reviews")
      .update({
        hidden_by_admin: data.hidden,
        hidden_reason: data.hidden ? (data.reason ?? null) : null,
      })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

/** Report a review for moderation. Any signed-in user can report each review once. */
export const reportReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => reportSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("review_reports").insert({
      review_id: data.reviewId,
      reported_by: context.userId,
      reason: data.reason,
      detail: data.detail?.trim() ? data.detail.trim() : null,
    });
    if (error) {
      // duplicate → surface friendly code
      if ((error as any).code === "23505") throw new Error("ALREADY_REPORTED");
      throw error;
    }
    return { ok: true };
  });

export type AdminReviewReport = {
  id: string;
  review_id: string;
  reported_by: string;
  reporter_email: string | null;
  reason: ReviewReportReason;
  detail: string | null;
  status: ReviewReportStatus;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  review: {
    id: string;
    target_type: "business" | "menu_item" | "supplier";
    target_id: string;
    rating: number;
    comment: string | null;
    author_name_snapshot: string | null;
    hidden_by_admin: boolean;
    user_id: string;
  } | null;
};

/** Admin / moderator: list all review reports. */
export const listReviewReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ status: z.enum(["open", "resolved", "dismissed", "all"]).default("open") }).parse(data),
  )
  .handler(async ({ data, context }): Promise<AdminReviewReport[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    const { data: isMod } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "moderator" });
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    let q = context.supabase
      .from("review_reports")
      .select(
        "id, review_id, reported_by, reason, detail, status, resolution_note, resolved_at, created_at, " +
          "reviews:review_id(id,target_type,target_id,rating,comment,author_name_snapshot,hidden_by_admin,user_id), " +
          "profiles:reported_by(email)",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.status !== "all") q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw error;

    return (rows ?? []).map((r: any) => ({
      id: r.id,
      review_id: r.review_id,
      reported_by: r.reported_by,
      reporter_email: r.profiles?.email ?? null,
      reason: r.reason,
      detail: r.detail,
      status: r.status,
      resolution_note: r.resolution_note,
      resolved_at: r.resolved_at,
      created_at: r.created_at,
      review: r.reviews
        ? {
            id: r.reviews.id,
            target_type: r.reviews.target_type,
            target_id: r.reviews.target_id,
            rating: r.reviews.rating,
            comment: r.reviews.comment,
            author_name_snapshot: r.reviews.author_name_snapshot,
            hidden_by_admin: r.reviews.hidden_by_admin,
            user_id: r.reviews.user_id,
          }
        : null,
    }));
  });

/** Admin / moderator: resolve or dismiss a report. */
export const resolveReviewReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => resolveReportSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    const { data: isMod } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "moderator" });
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    const patch = {
      status: data.status,
      resolution_note: data.note?.trim() ? data.note.trim() : null,
      resolved_by: data.status === "open" ? null : context.userId,
      resolved_at: data.status === "open" ? null : new Date().toISOString(),
    };
    const { error } = await context.supabase.from("review_reports").update(patch).eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export type RatingSummary = { targetId: string; avg: number; count: number };

/**
 * Public rating summaries (avg + count) for many targets at once.
 * Used by the map to render stars inside marker popups. Anon-scoped client,
 * so admin-hidden reviews are excluded by RLS.
 */
export const listRatingSummaries = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z
      .object({ targetType: z.enum(["business", "menu_item", "supplier"]).default("business") })
      .parse(data ?? {}),
  )
  .handler(async ({ data }): Promise<RatingSummary[]> => {
    const supa = await createPublicClient();
    const { data: rows, error } = await supa
      .from("reviews")
      .select("target_id, rating")
      .eq("target_type", data.targetType)
      .limit(10000);
    if (error) throw error;

    const acc = new Map<string, { sum: number; n: number }>();
    for (const r of (rows ?? []) as Array<{ target_id: string; rating: number }>) {
      const cur = acc.get(r.target_id) ?? { sum: 0, n: 0 };
      cur.sum += r.rating;
      cur.n += 1;
      acc.set(r.target_id, cur);
    }
    return [...acc.entries()].map(([targetId, v]) => ({
      targetId,
      avg: Math.round((v.sum / v.n) * 10) / 10,
      count: v.n,
    }));
  });

export type AdminReviewRow = {
  id: string;
  user_id: string;
  author_name_snapshot: string | null;
  author_email: string | null;
  target_type: ReviewTargetType;
  target_id: string;
  target_label: string | null;
  target_href: string | null;
  target_public: boolean;
  rating: number;
  comment: string | null;
  hidden_by_admin: boolean;
  hidden_reason: string | null;
  open_reports: number;
  created_at: string;
  updated_at: string;
};

/** Admin / moderator: list all reviews (visible + hidden) with target context. */
export const listAllReviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        visibility: z.enum(["all", "visible", "hidden"]).default("all"),
        targetType: z.enum(["all", "business", "menu_item", "supplier"]).default("all"),
        search: z.string().trim().max(200).optional(),
        limit: z.number().int().min(1).max(500).default(200),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data, context }): Promise<AdminReviewRow[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    const { data: isMod } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "moderator" });
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    let q = context.supabase
      .from("reviews")
      .select(
        "id, user_id, target_type, target_id, rating, comment, author_name_snapshot, hidden_by_admin, hidden_reason, created_at, updated_at",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.visibility !== "all") q = q.eq("hidden_by_admin", data.visibility === "hidden");
    if (data.targetType !== "all") q = q.eq("target_type", data.targetType);
    if (data.search) q = q.ilike("comment", `%${data.search}%`);

    const { data: rows, error } = await q;
    if (error) throw error;
    const reviews = (rows ?? []) as any[];
    if (reviews.length === 0) return [];

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const bizIds = [...new Set(reviews.filter((r) => r.target_type === "business").map((r) => r.target_id))];
    const supIds = [...new Set(reviews.filter((r) => r.target_type === "supplier").map((r) => r.target_id))];
    const itemIds = [...new Set(reviews.filter((r) => r.target_type === "menu_item").map((r) => r.target_id))];
    const userIds = [...new Set(reviews.map((r) => r.user_id))];
    const reviewIds = reviews.map((r) => r.id);

    const [bizRes, supRes, itemRes, profRes, repRes] = await Promise.all([
      bizIds.length
        ? supabaseAdmin.from("businesses").select("id, name, status").in("id", bizIds)
        : Promise.resolve({ data: [] as any[] }),
      supIds.length
        ? supabaseAdmin
            .from("business_suppliers")
            .select("id, name, business_id, businesses:business_id(status)")
            .in("id", supIds)
        : Promise.resolve({ data: [] as any[] }),
      itemIds.length
        ? supabaseAdmin
            .from("menu_items")
            .select("id, name, menu_categories!inner(menu_id, menus!inner(id, slug, status, business_id, businesses:business_id(status)))")
            .in("id", itemIds)
        : Promise.resolve({ data: [] as any[] }),
      supabaseAdmin.from("profiles").select("id, email, full_name").in("id", userIds),
      supabaseAdmin.from("review_reports").select("review_id, status").in("review_id", reviewIds),
    ]);

    const bizMap = new Map<string, any>((bizRes.data ?? []).map((b: any) => [b.id, b]));
    const supMap = new Map<string, any>((supRes.data ?? []).map((s: any) => [s.id, s]));
    const itemMap = new Map<string, any>((itemRes.data ?? []).map((i: any) => [i.id, i]));
    const profMap = new Map<string, any>((profRes.data ?? []).map((p: any) => [p.id, p]));
    const openReports = new Map<string, number>();
    for (const r of (repRes.data ?? []) as any[]) {
      if (r.status === "open") openReports.set(r.review_id, (openReports.get(r.review_id) ?? 0) + 1);
    }

    return reviews.map((r) => {
      let label: string | null = null;
      let href: string | null = null;
      let isPublic = false;
      if (r.target_type === "business") {
        const b = bizMap.get(r.target_id);
        label = b?.name ?? null;
        href = `/podnik/${r.target_id}`;
        isPublic = b?.status === "approved";
      } else if (r.target_type === "supplier") {
        const s = supMap.get(r.target_id);
        label = s?.name ?? null;
        href = s ? `/podnik/${s.business_id}/dodavatel/${r.target_id}` : null;
        isPublic = s?.businesses?.status === "approved";
      } else {
        const it = itemMap.get(r.target_id);
        const menu = it?.menu_categories?.menus;
        label = it?.name ?? null;
        href = menu?.business_id && menu?.slug ? `/podnik/${menu.business_id}/menu/${menu.slug}` : null;
        isPublic = menu?.status === "published" && menu?.businesses?.status === "approved";
      }
      const prof = profMap.get(r.user_id);
      return {
        id: r.id,
        user_id: r.user_id,
        author_name_snapshot: r.author_name_snapshot ?? prof?.full_name ?? null,
        author_email: prof?.email ?? null,
        target_type: r.target_type,
        target_id: r.target_id,
        target_label: label,
        target_href: href,
        target_public: isPublic,
        rating: r.rating,
        comment: r.comment,
        hidden_by_admin: r.hidden_by_admin === true,
        hidden_reason: r.hidden_reason ?? null,
        open_reports: openReports.get(r.id) ?? 0,
        created_at: r.created_at,
        updated_at: r.updated_at,
      } satisfies AdminReviewRow;
    });
  });

/** Admin: permanently delete a review. */
export const adminDeleteReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    const { data: isMod } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "moderator" });
    if (!isAdmin && !isMod) throw new Error("Forbidden");
    const { error } = await context.supabase.from("reviews").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });
